import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import {
  CalendarBlankIcon,
  CaretLeftIcon,
  CaretRightIcon
} from "@phosphor-icons/react";

import Input from "components/Input";
import { DateInputStyles } from "./styled";
import Icon from "components/Icon/Icon";

const DateInput = ({
  id,
  label,
  value,
  onChange,
  placeholder = "DD/MM/YYYY",
  maxDate = new Date(),
  minDate,
  required = false,
  size = "M"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const today = new Date();

  const selectedDate = (() => {
    if (!value) return null;
    const [year, month, day] = String(value).split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day); // local time, no UTC shift
  })();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <DateInputStyles>
      <div className="field date-field" ref={wrapperRef}>
        <Input
          id={id}
          label={label}
          value={selectedDate ? format(selectedDate, "dd/MM/yyyy") : ""}
          leftIcon={<CalendarBlankIcon weight="regular" color="#A5A5A5" />}
          placeholder={placeholder}
          readOnly
          required={required}
          size={size}
          onClick={() => setIsOpen(true)}
        />

        {isOpen && (
          <div className="date-dropdown">
            <DatePicker
              className="single-month-picker"
              calendarClassName="calendar-single-month"
              selected={selectedDate}
              inline
              maxDate={maxDate}
              minDate={minDate}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              renderCustomHeader={({
                monthDate,
                decreaseMonth,
                increaseMonth
              }) => {
                const isFutureMonth =
                  monthDate.getFullYear() > today.getFullYear() ||
                  (monthDate.getFullYear() === today.getFullYear() &&
                    monthDate.getMonth() > today.getMonth());

                const isNextMonthDisabled =
                  monthDate.getMonth() === today.getMonth() &&
                  monthDate.getFullYear() === today.getFullYear();

                return (
                  <div className="custom-datepicker-header">
                    <div onClick={decreaseMonth} className="nav-arrow prev">
                      <Icon component={CaretLeftIcon} size={16} />
                    </div>

                    <span className="month-label">
                      {monthDate.toLocaleString("default", {
                        month: "long",
                        year: "numeric"
                      })}
                    </span>

                    {!isFutureMonth && !isNextMonthDisabled && (
                      <div onClick={increaseMonth} className="nav-arrow next">
                        <Icon component={CaretRightIcon} size={16} />
                      </div>
                    )}
                  </div>
                );
              }}
              onChange={(date) => {
                setIsOpen(false);
                if (!date) return onChange?.("");
                // Pass formatted date string directly, not ISO
                onChange?.(format(date, "yyyy-MM-dd")); // ← "1990-11-02"
              }}
            />
          </div>
        )}
      </div>
    </DateInputStyles>
  );
};

export default DateInput;
